import { Button, Field, SignatureArt } from './ui/controls';
import React, { useState, FormEvent } from 'react';
import { signInWithEmailAndPassword, AuthError } from 'firebase/auth';
import { auth } from '../firebase/config';
import { Link, useNavigate } from 'react-router-dom';
import SocialLogin from './SocialLogin';
import { useTranslation } from 'react-i18next';

const Login: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/sudoku');
    } catch (error) {
      const authError = error as AuthError;
      setError(authError.message);
    }
  };

  return (
    <div className="mx-auto grid max-w-6xl items-start gap-12 px-4 py-8 sm:px-6 lg:grid-cols-2 lg:py-16">
      <div className="mx-auto w-full max-w-md space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            {t('auth.login')}
          </h2>
        </div>
        {error && <p role="alert" className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950 dark:text-red-200">{error}</p>}
        <form className="mt-8 space-y-6" onSubmit={handleLogin}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email-address" className="mb-2 block text-sm font-semibold">
                {t('auth.email')}
              </label>
              <Field
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                placeholder={t('auth.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="mb-2 block text-sm font-semibold">
                {t('auth.password')}
              </label>
              <Field
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                placeholder={t('auth.password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Button
              type="submit"
              className="w-full"
            >
              {t('auth.signIn')}
            </Button>
          </div>
        </form>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-chalk dark:bg-midnight text-slate-600 dark:text-slate-300">
                {t('auth.orContinueWithGoogle')}
              </span>
            </div>
          </div>

          <SocialLogin />
        </div>

        <div className="text-center text-gray-600 dark:text-gray-300">
          <p>
            {t('auth.dontHaveAccount')}{' '}
            <Link to="/signup" className="text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300">
              {t('auth.signup')}
            </Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block">
        <SignatureArt className="aspect-square" />
        <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">{t('brand.origin')}</p>
      </div>
    </div>
  );
};

export default Login;
